import { OcrFormat } from "./types.js";
import { filterOcrWords, ocrClearFilledWarnings, ocrLines, ocrWordsFromTsv, } from "./shared.js";
import { ocrParseBoard } from "./truckstop_board.js";
import { ocrParseBennett } from "./bennett.js";
import { ocrParseBME } from "./bme.js";
import { ocrParseTsDetail } from "./truckstop_detail.js";
import { ocrParseRateCons } from "./ratecon.js";
import { ocrPair } from "./merge.js";
import { ocrFuelFromPasses } from "./fuel.js";
function loadToFields(L) {
    const { _cy: _ignored, ...rest } = L;
    void _ignored;
    return { ...rest };
}
function cleanLoads(loads) {
    return loads.map((L) => {
        delete L._cy;
        ocrClearFilledWarnings(L);
        L.unreadable = L.unreadable.filter((u, i, arr) => arr.indexOf(u) === i);
        return L;
    });
}
function score(first) {
    const filled = ["linehaul", "origin", "destination", "equipment", "miles"].filter((k) => String(first[k] || "").trim()).length;
    // Ours (not in index.html): heuristic for UI review priority.
    return Math.min(1, 0.35 + filled * 0.12);
}
function finalize(loads, format, kind) {
    const cleaned = cleanLoads(loads);
    const first = cleaned[0];
    return {
        format,
        fields: {
            loads: cleaned.map(loadToFields),
            load: loadToFields(first),
            kind,
        },
        confidence: score(first),
    };
}
function rateconFormat(kind) {
    if (/Trinity/i.test(kind))
        return OcrFormat.trinity;
    if (/Evans/i.test(kind))
        return OcrFormat.evans;
    if (/Landstar/i.test(kind))
        return OcrFormat.landstar;
    return OcrFormat.ratecon;
}
/**
 * Dispatch matching index.html ocrReadScreenshot order:
 * ratecon (plainText block+sparse) → bme (block-primary) → truckstop_detail (sparse-primary)
 * → bennett (sparse-primary) → board (block-primary).
 * Source ratecon does not use ocrPair; it joins lb then ls into one plainText.
 */
function dispatchLoads(blockWords, sparseWords) {
    const blockLines = ocrLines(blockWords);
    const sparseLines = ocrLines(sparseWords);
    // Source: plainText = lb.join + '\n' + ls.join; ocrParseRateCons(plainText)
    const plainText = blockLines.map((l) => l.text).join("\n") + "\n" + sparseLines.map((l) => l.text).join("\n");
    const rc = ocrParseRateCons(plainText);
    if (rc.loads.length)
        return finalize(rc.loads, rateconFormat(rc.kind), rc.kind);
    // Source: ocrPair(ocrParseBME(lb), ocrParseBME(ls)) — block primary.
    const bme = ocrPair(ocrParseBME(blockLines), ocrParseBME(sparseLines));
    if (bme.length)
        return finalize(bme, OcrFormat.bme, "Bennett load page");
    // Source: ocrPair(ocrParseTsDetail(ls, wsp), ocrParseTsDetail(lb, wb)) — sparse primary.
    const det = ocrPair(ocrParseTsDetail(sparseLines, sparseWords), ocrParseTsDetail(blockLines, blockWords));
    if (det.length)
        return finalize(det, OcrFormat.truckstop_detail, "Truckstop load page");
    // Source: ocrPair(ocrParseBennett(ls), ocrParseBennett(lb)) — sparse primary.
    const ben = ocrPair(ocrParseBennett(sparseLines), ocrParseBennett(blockLines));
    if (ben.length)
        return finalize(ben, OcrFormat.bennett, "Bennett load table");
    // Source: ocrPair(ocrParseBoard(lb), ocrParseBoard(ls)) — block primary.
    const brd = ocrPair(ocrParseBoard(blockLines), ocrParseBoard(sparseLines));
    if (brd.length)
        return finalize(brd, OcrFormat.truckstop_board, "Truckstop load board");
    return { format: OcrFormat.unknown, fields: { kind: "" }, confidence: 0 };
}
/**
 * Loads first (source order), then fuel from both reads, as in ocrReadScreenshot.
 * Fuel rides along on every result; with no loads it becomes a fuel_board result.
 */
function dispatchFromPasses(blockWords, sparseWords) {
    const res = dispatchLoads(blockWords, sparseWords);
    const fuel = ocrFuelFromPasses(ocrLines(sparseWords), ocrLines(blockWords));
    if (res.format === OcrFormat.unknown && fuel.length) {
        // confidence 0.6 is ours (not in index.html), like score().
        return { format: OcrFormat.fuel_board, fields: { kind: "fuel price list", fuel }, confidence: 0.6 };
    }
    return { ...res, fields: { ...res.fields, fuel } };
}
/** Two-read path from TSV (PSM 6 block + PSM 11 sparse), as in ocrReadScreenshot. */
export function parseOcrTsv(tsvBlock, tsvSparse) {
    return dispatchFromPasses(ocrWordsFromTsv(tsvBlock), ocrWordsFromTsv(tsvSparse));
}
/**
 * @deprecated Prefer parseOcrTsv / parseOcrWordPasses when both PSM 6 and PSM 11 passes exist.
 * Single word-list path (legacy callers). Uses the same list as both reads so ocrPair still runs.
 */
export function parseOcrWords(words) {
    const layout = filterOcrWords(words);
    return dispatchFromPasses(layout, layout);
}
/** Two word lists from PSM 6 / PSM 11 recognizes (no TSV required). */
export function parseOcrWordPasses(blockWords, sparseWords) {
    return dispatchFromPasses(filterOcrWords(blockWords), filterOcrWords(sparseWords));
}
/** Direct rate-confirmation text path (source parseHaulOcrText / pasted ratecons). */
export function parseRateConText(text) {
    const rc = ocrParseRateCons(text);
    if (!rc.loads.length)
        return { format: OcrFormat.unknown, fields: { kind: "" }, confidence: 0 };
    return finalize(rc.loads, rateconFormat(rc.kind), rc.kind);
}
//# sourceMappingURL=parse.js.map